import React, { useCallback, useEffect, useRef, useState } from 'react';
import { E_GET_CAPTCHA, emitter } from '../../event';
import './style.css';

type CaptchaPayload = {
    show?: boolean;
    digit?: number;
    seed?: number;
    error?: number;
};

const parsePayload = (payload: any): CaptchaPayload | null => {
    try {
        const raw = Array.isArray(payload) ? payload[0] : payload;
        if (typeof raw !== 'string') return null;
        return JSON.parse(raw);
    } catch (error) {
        console.error('Captcha JSON error', error);
        return null;
    }
};

const createSeededRandom = (seed: number) => {
    let state = seed >>> 0;
    return () => {
        state += 0x6d2b79f5;
        let next = Math.imul(state ^ (state >>> 15), 1 | state);
        next ^= next + Math.imul(next ^ (next >>> 7), 61 | next);
        return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
    };
};

const drawCaptcha = (canvas: HTMLCanvasElement, digit: string, seed: number) => {
    const context = canvas.getContext('2d');
    if (!context) return;

    const width = 420;
    const height = 150;
    const dpr = Math.max(window.devicePixelRatio || 1, 1);

    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, width, height);

    const random = createSeededRandom(seed);

    const background = context.createLinearGradient(0, 0, width, height);
    background.addColorStop(0, '#101723');
    background.addColorStop(1, '#1b2a40');
    context.fillStyle = background;
    context.fillRect(0, 0, width, height);

    for (let i = 0; i < 24; i++) {
        context.beginPath();
        context.strokeStyle = `rgba(255,255,255,${0.06 + random() * 0.2})`;
        context.lineWidth = 1 + random() * 2;
        context.moveTo(random() * width, random() * height);
        context.lineTo(random() * width, random() * height);
        context.stroke();
    }

    for (let i = 0; i < 60; i++) {
        context.beginPath();
        context.fillStyle = `rgba(255,255,255,${0.04 + random() * 0.22})`;
        context.arc(random() * width, random() * height, 0.8 + random() * 2.2, 0, Math.PI * 2);
        context.fill();
    }

    context.save();
    context.translate(width * 0.5 + (random() - 0.5) * 18, height * 0.58 + (random() - 0.5) * 12);
    context.rotate((random() - 0.5) * 0.35);
    context.scale(1.12, 0.9 + random() * 0.15);
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = '900 112px "TT Commons", "Arial Black", sans-serif';

    const digitGradient = context.createLinearGradient(-50, -70, 50, 70);
    digitGradient.addColorStop(0, '#f4f8ff');
    digitGradient.addColorStop(1, '#b8ccff');

    context.fillStyle = digitGradient;
    context.shadowColor = 'rgba(120, 170, 255, 0.5)';
    context.shadowBlur = 16;
    context.fillText(digit, 0, 0);

    context.lineWidth = 3;
    context.strokeStyle = 'rgba(26, 44, 74, 0.9)';
    context.shadowBlur = 0;
    context.strokeText(digit, 0, 0);
    context.restore();

    for (let i = 0; i < 4; i++) {
        context.beginPath();
        context.strokeStyle = `rgba(44, 220, 170, ${0.22 + random() * 0.28})`;
        context.lineWidth = 1 + random() * 2;
        context.moveTo(0, random() * height);
        context.bezierCurveTo(
            width * (0.25 + random() * 0.2),
            random() * height,
            width * (0.65 + random() * 0.2),
            random() * height,
            width,
            random() * height
        );
        context.stroke();
    }
};

const Captcha = () => {
    const [visible, setVisible] = useState(false);
    const [digit, setDigit] = useState('0');
    const [seed, setSeed] = useState(1);
    const [input, setInput] = useState('');
    const [hasError, setHasError] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const submitAnswer = useCallback(() => {
        if (!visible) return;
        window.cef.emit('cef:captchaAction', `submit|${input.trim()}`);
    }, [visible, input]);

    useEffect(() => {
        const unsubscribe = emitter.subscribe(E_GET_CAPTCHA, (payload: any) => {
            const result = parsePayload(payload);
            if (!result) return;

            if (!result.show) {
                setVisible(false);
                setHasError(false);
                setInput('');
                window.cef.set_focus(false);
                return;
            }

            if (typeof result.digit === 'number') setDigit(String(result.digit));
            setSeed(typeof result.seed === 'number' ? result.seed : Date.now());
            setHasError(result.error === 1);
            if (result.error !== 1) setInput('');
            setVisible(true);
            window.cef.set_focus(true);
        });

        return () => {
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (!visible) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (!visible) return;

            if (event.keyCode === 13) {
                event.preventDefault();
                submitAnswer();
                return;
            }

            if (event.keyCode === 27) {
                event.preventDefault();
                window.cef.emit('cef:captchaAction', 'cancel');
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [visible, submitAnswer]);

    useEffect(() => {
        if (!visible || !canvasRef.current) return;
        drawCaptcha(canvasRef.current, digit, seed);
    }, [visible, digit, seed]);

    if (!visible) return null;

    return (
        <div className="cef-captcha">
            <div className="cef-captcha-panel">
                <h2>Проверка на бота</h2>
                <p>Введите цифру с изображения</p>

                <canvas ref={canvasRef} className="cef-captcha-canvas" />

                {hasError && <div className="cef-captcha-error">Нужно ввести одну цифру</div>}

                <div className="cef-captcha-actions">
                    <input
                        value={input}
                        onChange={(event) => setInput(event.target.value.replace(/[^0-9]/g, '').slice(0, 1))}
                        maxLength={1}
                        inputMode="numeric"
                        autoFocus
                        placeholder="0-9"
                    />
                    <button type="button" onClick={submitAnswer}>Подтвердить</button>
                </div>
            </div>
        </div>
    );
};

export default Captcha;
